import { useState, useEffect } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Password Generator — uses crypto.getRandomValues() for cryptographic
// randomness (not Math.random which is predictable and not suitable for
// passwords). Shows entropy bits explicitly so the user can judge.
//
// Entropy bits = length × log2(charset_size). 80 bits is considered
// strong against offline brute-force; 128 bits is "future-proof."

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>?/";
const AMBIGUOUS = /[0O1lI|]/g;

const secureRandomInt = (max) => {
  // Reject-sample to avoid modulo bias
  const range = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  while (true) {
    crypto.getRandomValues(buf);
    if (buf[0] < range) return buf[0] % max;
  }
};

const generate = (length, charset) => {
  if (!charset || charset.length === 0) return "";
  let out = "";
  for (let i = 0; i < length; i++) out += charset[secureRandomInt(charset.length)];
  return out;
};

const entropyBits = (length, charsetSize) => {
  if (!length || !charsetSize) return 0;
  return length * Math.log2(charsetSize);
};

const strengthTier = (bits) => {
  if (bits < 40) return { label: "Weak", color: "#E0584E" };
  if (bits < 60) return { label: "Fair", color: "#F5A299" };
  if (bits < 80) return { label: "Strong", color: "#F8B84E" };
  if (bits < 128) return { label: "Very strong", color: "#5BD99A" };
  return { label: "Excellent", color: "#5BD99A" };
};

export default function PasswordGenerator() {
  const [length, setLength] = useState(20);
  const [useLower, setUseLower] = useState(true);
  const [useUpper, setUseUpper] = useState(true);
  const [useDigits, setUseDigits] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [excludeAmbig, setExcludeAmbig] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const charset = (() => {
    let cs = "";
    if (useLower) cs += LOWER;
    if (useUpper) cs += UPPER;
    if (useDigits) cs += DIGITS;
    if (useSymbols) cs += SYMBOLS;
    if (excludeAmbig) cs = cs.replace(AMBIGUOUS, "");
    return cs;
  })();
  const bits = entropyBits(length, charset.length);
  const tier = strengthTier(bits);

  const gen = () => { setPassword(generate(length, charset)); setCopied(false); };

  // Generate one on mount and whenever options change
  useEffect(() => { gen(); /* eslint-disable-next-line */ }, [length, useLower, useUpper, useDigits, useSymbols, excludeAmbig]);

  const copy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <ToolsShell title="Password Generator">
      {/* Output box */}
      <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "20px 22px", marginTop: 4 }}>
        <div style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: 18, color: TOOLS_ACCENT, fontWeight: 600, minHeight: "1.5em" }}>
          {password || "—"}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={gen} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Regenerate</button>
          <button onClick={copy} style={{ background: copied ? "#5BD99A" : TOOLS_ACCENT, color: copied ? "#0A0B14" : "#1A0604", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {copied ? "✓ Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* Strength meter */}
      <div style={{ marginTop: 14, padding: "12px 16px", background: "#13141F", border: "1px solid #262839", borderRadius: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Entropy</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: tier.color }}>{tier.label}</div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: tier.color, fontVariantNumeric: "tabular-nums" }}>
          {bits.toFixed(0)} <span style={{ fontSize: 13, color: "#9A9DBE", fontWeight: 400 }}>bits</span>
        </div>
        <div style={{ height: 6, background: "#0A0B14", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(100, (bits / 128) * 100)}%`, height: "100%", background: tier.color, transition: "width 0.15s" }} />
        </div>
      </div>

      {/* Length slider */}
      <div style={{ marginTop: 16 }}>
        <label style={{ fontSize: 13, color: "#9A9DBE", display: "flex", justifyContent: "space-between" }}>
          <span>Length</span>
          <span style={{ color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{length}</span>
        </label>
        <input type="range" min="8" max="64" value={length} onChange={(e) => setLength(parseInt(e.target.value, 10))} style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 6 }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#5B5E7A" }}>
          <span>8</span><span>20</span><span>32</span><span>64</span>
        </div>
      </div>

      {/* Character class toggles */}
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Toggle checked={useLower} onChange={setUseLower} label="Lowercase" hint="abc" />
        <Toggle checked={useUpper} onChange={setUseUpper} label="Uppercase" hint="ABC" />
        <Toggle checked={useDigits} onChange={setUseDigits} label="Digits" hint="123" />
        <Toggle checked={useSymbols} onChange={setUseSymbols} label="Symbols" hint="!@#" />
      </div>
      <div style={{ marginTop: 8 }}>
        <Toggle checked={excludeAmbig} onChange={setExcludeAmbig} label="Exclude ambiguous (0/O, 1/l/I)" />
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 16 }}>
        Generated with <code style={{ color: "#9A9DBE" }}>crypto.getRandomValues()</code> — cryptographic-quality randomness, never <code>Math.random</code>. For most use, 16+ characters with all four classes (≥ 80 bits) is more than strong enough. Pair with a password manager.
      </p>
    </ToolsShell>
  );
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#13141F", border: `1px solid ${checked ? TOOLS_ACCENT + "55" : "#262839"}`, borderRadius: 10, cursor: "pointer", transition: "border-color 0.15s" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: TOOLS_ACCENT, width: 16, height: 16 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: "#E8E9F3", fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "#7C8099", fontFamily: "monospace" }}>{hint}</div>}
      </div>
    </label>
  );
}
